import { BannerMediaFiles } from '../../../../../types/brands';
import { CreativityType } from '../../../types/banner';
import type { RoutedFormData } from '../../../types/templates';
import { UploadPosterFn, useUploadMedia } from '../../../hooks/media/use-upload-media';
import { getHandlerForTemplate } from '../../../handlers';
import {
  uploadFormFiles,
  resolveBannerType,
  buildCleanConfig
} from '../../../hooks/banners/creation/form/submit-helpers';
import { useTemplateConfig } from '../../../hooks/banners/use-template-config';
import {
  renderResolutionsToBlobs,
  canvasJsonToModel,
  type CanvasJSON,
  type PieceModel,
  type ResolutionConfig
} from '@doer/frontend-components/creativity-editor';
import { EditorModelInput } from '../../../../../constructs/brands/banners/save-editor-banner/interface';
import { findResolutionForField } from '../../../utils';

type ProgressFn = (step: string, percent: number) => void;
type UploadVideoFn = ReturnType<typeof useUploadMedia>['uploadVideoDirect'];
type ClientHandler = ReturnType<typeof getHandlerForTemplate>;
type Template = ReturnType<typeof useTemplateConfig>;

export function autoSwitchToFirstInvalidResolution(
  errors: Record<string, string>,
  template: Template,
  activeResolutionId: string,
  setActiveResolutionId: (id: string) => void
): void {
  const firstInvalidKey = Object.keys(errors)[0];
  if (!firstInvalidKey || !template) return;
  const fieldResolution = findResolutionForField(template.formFields, firstInvalidKey);
  if (fieldResolution && fieldResolution !== activeResolutionId) {
    setActiveResolutionId(fieldResolution);
  }
}

export async function collectEditorModels(params: {
  resolutions: ResolutionConfig[];
  activeResolutionId: string;
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;
  getActiveModel: () => PieceModel | null;
}): Promise<EditorModelInput[]> {
  return Promise.all(
    params.resolutions.map(async (resolution): Promise<EditorModelInput> => {
      const active = resolution.id === params.activeResolutionId ? params.getActiveModel() : null;
      const model =
        active ??
        (await canvasJsonToModel(await params.resolveResolutionJson(resolution), {
          width: resolution.canvasWidth,
          height: resolution.canvasHeight,
          type: resolution.type
        }));
      return { resolutionId: resolution.id, model: JSON.stringify(model) };
    })
  );
}

export async function prepareBannerData(params: {
  formData: RoutedFormData;
  clientHandler: ClientHandler;
  creativityType: CreativityType;
  clientId: string;
  bannerId: string;
  uploadVideoDirect: UploadVideoFn;
  onProgress: ProgressFn;
}) {
  params.onProgress('Subiendo archivos del formulario...', 10);
  const processed = await uploadFormFiles(params.formData, params.uploadVideoDirect, params.clientId, params.bannerId);
  const finalBannerType = resolveBannerType(params.clientHandler, processed, params.creativityType);
  const cleanedConfig = buildCleanConfig(params.clientHandler, processed, params.creativityType, finalBannerType);

  // Optional; falls back to the backend default when the template doesn't implement it.
  const folder = params.clientHandler.buildFolder?.(processed, finalBannerType);
  return { processed, finalBannerType, cleanedConfig, folder };
}

export async function renderPreviews(params: {
  resolutions: ResolutionConfig[];
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;
  onProgress: ProgressFn;
}) {
  const imageResolutionsCount = params.resolutions.filter(r => r.type === 'image').length;
  params.onProgress(`Renderizando previews (0/${imageResolutionsCount})...`, 25);

  return renderResolutionsToBlobs(params.resolutions, params.resolveResolutionJson, (current, total) => {
    params.onProgress(`Renderizando previews (${current}/${total})...`, 25 + Math.round((current / total) * 25));
  });
}

export async function uploadPreviews(params: {
  rendered: Array<{ resolution: ResolutionConfig; blob: Blob }>;
  clientHandler: ClientHandler;
  processed: RoutedFormData;
  finalBannerType: string;
  clientId: string;
  bannerId: string;
  uploadPosterDirect: UploadPosterFn;
  onProgress: ProgressFn;
}): Promise<BannerMediaFiles[]> {
  const uploadTotal = params.rendered.length;
  params.onProgress(`Subiendo imágenes (0/${uploadTotal})...`, 50);

  const formDataForNaming = { ...params.processed, _creativityType: params.finalBannerType };
  let uploadedCount = 0;

  return Promise.all(
    params.rendered.map(({ resolution, blob }) => {
      const fileNamingPrefix = params.clientHandler.getFileNaming?.(formDataForNaming, resolution);
      return uploadResolutionAsset(
        resolution,
        blob,
        params.uploadPosterDirect,
        params.clientId,
        params.bannerId,
        fileNamingPrefix
      ).then(result => {
        uploadedCount++;
        params.onProgress(
          `Subiendo piezas (${uploadedCount}/${uploadTotal})...`,
          50 + Math.round((uploadedCount / uploadTotal) * 30)
        );
        return result;
      });
    })
  );
}

async function uploadResolutionAsset(
  resolution: ResolutionConfig,
  fullBlob: Blob,
  uploadPoster: UploadPosterFn,
  clientId: string,
  bannerId: string,
  fileNamingPrefix?: string
): Promise<BannerMediaFiles> {
  const rawNaming = fileNamingPrefix?.replace(/\.[^.]+$/, '') ?? '';
  const namingWithoutExt = rawNaming || resolution.id;
  const baseName = `${bannerId}--${namingWithoutExt}`;
  const fullFile = new File([fullBlob], fileNamingPrefix ?? '', { type: 'image/png' });
  const { publicUrl } = await uploadPoster('image', '.png', fullFile, baseName, clientId, false);

  return {
    type: 'image',
    url: publicUrl,
    ...(resolution.aspectRatioCode && { format: resolution.aspectRatioCode as BannerMediaFiles['format'] }),
    ...(fileNamingPrefix && { fileNamingPrefix })
  };
}
